import {
  chatSummaryUpdatedDossbotEventDataSchema,
  chatTitleUpdatedDossbotEventDataSchema,
} from '@doss/common-all'
import { RxPencil2 } from 'react-icons/rx'
import { useNavigate } from 'react-router'
import { apolloClient } from '~/common/apollo-client'
import {
  DossbotEventType,
  useDossbotChatsQuery,
  usePushDossbotEventSubscription,
} from '~/common/generated/apollo-operations'
import { DossbotLeftNavItem } from '~/components/dossbot/DossbotLeftNavItem'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'

interface DossbotChatProps {
  chatId?: string
}

/**
 * This is the home page for the Dossbot application.
 * It renders the CopilotKit and CopilotChat components with a sidebar listing all chats.
 */
export function DossbotLeftNav({ chatId }: DossbotChatProps) {
  const { data, refetch } = useDossbotChatsQuery()
  const navigate = useNavigate()

  usePushDossbotEventSubscription({
    variables: { chatId: chatId ?? '' },
    onData: ({ data }) => {
      if (!data.data) {
        return
      }

      const event = data.data.pushDossbotEvent

      switch (event.type) {
        case DossbotEventType.CHAT_SUMMARY_UPDATED: {
          const parsedEvent = chatSummaryUpdatedDossbotEventDataSchema.parse(event.payload)
          apolloClient.cache.modify({
            id: `DossbotChat:${chatId}`,
            fields: {
              summary: () => parsedEvent.summary,
            },
          })
          break
        }
        case DossbotEventType.CHAT_TITLE_UPDATED: {
          const parsedEvent = chatTitleUpdatedDossbotEventDataSchema.parse(event.payload)
          apolloClient.cache.modify({
            id: `DossbotChat:${chatId}`,
            fields: {
              title: () => parsedEvent.title,
            },
          })
          break
        }
        case DossbotEventType.CHAT_RUN_STARTED:
          // Refetch to update the sort order based on the new run
          refetch()
          break
        case DossbotEventType.CHAT_RUN_COMPLETED:
        case DossbotEventType.CHAT_RUN_FAILED:
        case DossbotEventType.CHAT_RUN_CANCELED:
        case DossbotEventType.TOOL_RUN_STARTED:
        case DossbotEventType.TOOL_RUN_COMPLETED:
        case DossbotEventType.TOOL_RUN_FAILED:
        case DossbotEventType.TOOL_RUN_CANCELED:
        case DossbotEventType.TOOL_RUN_PENDING:
        case DossbotEventType.TOOL_RUN_APPROVED:
        case DossbotEventType.TOOL_RUN_REJECTED:
        case DossbotEventType.AGENT_RUN_COMPLETED:
        case DossbotEventType.AGENT_RUN_STARTED:
        case DossbotEventType.AGENT_RUN_FAILED:
        case DossbotEventType.AGENT_RUN_CANCELED:
          break
      }
    },
  })

  return (
    <div className="flex h-full">
      {/* Left Sidebar */}
      <div className="w-72 overflow-auto border-r border-gray-4 p-2">
        <div className="flex items-center justify-between p-2 pb-3">
          <div className="text-xl text-gray-10">Chats</div>
          <div className="flex cursor-pointer items-center gap-2">
            <Tooltip placement="bottom" title="Create new chat">
              <button
                className="text-gray-600 hover:text-blue-600 rounded-full p-2 transition-colors duration-200 hover:bg-gray-4"
                onClick={() => navigate('/dossbot/chat/new')}
              >
                <RxPencil2 size={18} />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {data?.dossbotChats.map((chat) => {
            return <DossbotLeftNavItem key={chat.id} chat={chat} chatId={chatId} />
          })}
        </div>
      </div>
    </div>
  )
}
